import React from 'react';
import styled, { keyframes } from 'styled-components';
import { colors, gradients, spacing } from '../../constants/theme';
import logoUrl from '../../assets/intuition_ai_logo.png';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const LoadingContainer = styled.div`
  min-height: 100vh;
  background: ${gradients.background};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${spacing.xl};
  animation: ${fadeIn} 0.6s ease-out;
`;

const LoadingCard = styled.div`
  background: ${colors.background.paper};
  border-radius: 20px;
  padding: ${spacing.xxl};
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.3);
  border: 1px solid ${colors.background.tertiary};
  text-align: center;
  max-width: 500px;
  width: 100%;
`;

const Logo = styled.img`
  width: 80px;
  height: auto;
  margin-bottom: ${spacing.xl};
  opacity: 0.9;
`;

const Title = styled.h1`
  color: ${colors.text.primary};
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: ${spacing.md};
  background: ${gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Message = styled.p`
  color: ${colors.text.secondary};
  font-size: 1rem;
  margin-bottom: ${spacing.xl};
  line-height: 1.6;
`;

const LoadingBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${colors.background.tertiary};
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: ${spacing.lg};
`;

const LoadingProgress = keyframes`
  0% { transform: translateX(-100%); }
  50% { transform: translateX(0%); }
  100% { transform: translateX(100%); }
`;

const LoadingIndicator = styled.div`
  width: 50%;
  height: 100%;
  background: ${gradients.primary};
  border-radius: 2px;
  animation: ${LoadingProgress} 2s ease-in-out infinite;
`;

const LoadingSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

const LoadingStep = styled.div<{ completed?: boolean; active?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  border-radius: 8px;
  background: ${props => 
    props.completed ? 'rgba(16, 185, 129, 0.1)' :
    props.active ? 'rgba(99, 102, 241, 0.1)' :
    'transparent'
  };
  transition: all 0.3s ease;
  
  .step-icon {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${props => 
      props.completed ? colors.success :
      props.active ? colors.primary.main :
      colors.background.tertiary
    };
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 10px;
    font-weight: bold;
  }
  
  .step-text {
    color: ${props => 
      props.completed ? colors.success :
      props.active ? colors.primary.main :
      colors.text.secondary
    };
    font-size: 0.9rem;
    font-weight: ${props => props.active ? '600' : '400'};
  }
`;

interface DashboardLoadingProps {
  step?: number;
  totalSteps?: number;
}

const DashboardLoading: React.FC<DashboardLoadingProps> = ({ 
  step = 1, 
  totalSteps = 3 
}) => {
  const steps = [
    'Authenticating your account',
    'Loading your company data',
    'Preparing your dashboard'
  ];

  return (
    <LoadingContainer>
      <LoadingCard>
        <Logo src={logoUrl} alt="Intuition AI" />
        <Title>Loading your Dashboard</Title>
        <Message>
          We're setting up your personalized dashboard with all your company data and settings.
        </Message>
        
        <LoadingBar>
          <LoadingIndicator />
        </LoadingBar>
        
        <LoadingSteps>
          {steps.map((stepText, index) => (
            <LoadingStep 
              key={index}
              completed={index < step - 1}
              active={index === step - 1}
            >
              <div className="step-icon">
                {index < step - 1 ? '✓' : index + 1}
              </div>
              <div className="step-text">{stepText}</div>
            </LoadingStep>
          ))}
        </LoadingSteps>
      </LoadingCard>
    </LoadingContainer>
  );
};

export default DashboardLoading;

export {};  // Make this file a module
